import {
  CONNECTED_EVENT_DATA,
  PROTICO_AUTH_EVENTS,
  ProticoAuthCore,
  SUPPORTED_ADAPTERS,
  SUPPORTED_ADAPTER_TYPE,
  UserInfoData,
  Web3AccessToken,
} from "protico-sdk";
import { clearState, loadState } from "../../utils/localStorage.utils";

import React from "react";
import { AbstractProvider } from "web3-core";
import { getSecrets } from "../../config";
import { useAppDispatch, useAppSelector } from "../../hooks";
import {
  setAuthenticateStatus,
  setCurrentAddress,
} from "../../store/reducers/auth";
import { AuthenticateState } from "../../types/types";
import { getProticoClient } from "../../utils/api";
import { LocalStorageKeys } from "../../utils/strings.utils";
import { localStorageEnalbed } from "../../utils/storage.utils";
import { useRouter } from "../../hooks/useRouter";
import { useWhiteboardRoom } from "../../hooks/useWhiteboardRoom";
import { parseUrl } from "../../utils/format.utils";
import { useLocation } from "react-router-dom";
interface IWeb3AuthContext {
  login: (adapter: SUPPORTED_ADAPTER_TYPE) => Promise<void>;
  loginAsGuest: () => Promise<void>;
  logout: (route?: string, showModal?: boolean) => Promise<void>;
  switchChain: (chainId: string) => Promise<void>;
  logined: boolean;
  address: string | undefined;
  provider: AbstractProvider | undefined;
  chainId: string | undefined;
  connectedAdapterName: SUPPORTED_ADAPTER_TYPE | null;
  isProticoAuthenticated: boolean;
  proticoAuthenticate: () => Promise<void>;
  proticoGoogleAuthenticate: (
    userToken: string,
    exp: number,
    address: string
  ) => Promise<void>;
  isInited: boolean;
  userInfo: UserInfoData | null;
  realAddress: string | null;
  currentToken: Web3AccessToken | null;
  showModal: boolean;
  setShowModal?: (show: boolean) => void;
}

export const Web3AuthContext = React.createContext<IWeb3AuthContext>({
  login: undefined,
  loginAsGuest: undefined,
  logout: undefined,
  switchChain: undefined,
  logined: false,
  address: undefined,
  provider: undefined,
  chainId: undefined,
  connectedAdapterName: null,
  isProticoAuthenticated: false,
  proticoAuthenticate: undefined,
  proticoGoogleAuthenticate: undefined,
  isInited: false,
  userInfo: null,
  realAddress: null,
  currentToken: null,
  showModal: false,
  setShowModal: undefined,
});

export const Web3AuthProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const location = useLocation();
  const dispatch = useAppDispatch();
  const { NODE_ENV, DAPP_DOMAIN } = getSecrets();
  const useTestNet = NODE_ENV !== "production";
  const clientId = useTestNet
    ? "BPXN-RGCKzze9_K0uaibdvv0Db3z4b2Dz3_Z3es-kRcP0EZHJzyxIxAjQ3BJBwxZTpbs08y6-ivF8JeTBbKHUmA"
    : "BFPiST2ItVvoPPQIe9ym4M_BfVKYPftXaTbwfvfkErUEEoDxm-xkjVC9B8YjefCVjK143vGQ1f62dNopeShKuqo"; // get from https://dashboard.web3auth.io
  const web3AuthNetwork = useTestNet ? "testnet" : "mainnet";
  const wcProjectId = "4b2c0fa0796ebdf722ab16ac098bc11f";
  var proticoAuth = React.useRef<ProticoAuthCore>();
  const [provider, setProvider] = React.useState<AbstractProvider | null>();
  var providerRef = React.useRef(provider);
  const [chainId, setChainId] = React.useState<string | null>(null);
  const [address, setAddress] = React.useState<string | null>(null);
  const [realAddress, setRealAddress] = React.useState<string | null>(null);
  const [isInited, setIsInited] = React.useState<boolean>(false);
  const [userInfo, setUserInfo] = React.useState<UserInfoData | null>(null);
  const [previousPage, setPreviousPage] = React.useState<string | null>(null);
  const [showModal, setShowModal] = React.useState<boolean>(false);
  const [currentToken, setCurrentToken] =
    React.useState<Web3AccessToken | null>(null);
  const { push } = useRouter();
  const { roomDomain, pageUrl } = useWhiteboardRoom();
  interface RouterQuery {
    protico_disable?: string;
    [key: string]: any;
  }
  const { query } = useRouter() as { query: RouterQuery };
  const handleSetAddress = React.useCallback(
    (address: string, realAddress: string) => {
      setAddress(address);
      setRealAddress(realAddress);
      // TODO: remove this line and replace currentAddress with context in other places
      dispatch(setCurrentAddress(address ?? ""));
    },
    [dispatch]
  );
  const [isProticoAuthenticated, setIsProticoAuthenticated] =
    React.useState<boolean>(false);
  const [connectedAdapterName, setConnectedAdapterName] =
    React.useState<SUPPORTED_ADAPTER_TYPE | null>(null);
  const handleSetConnectedAdapterName = React.useCallback(
    (adapterName: SUPPORTED_ADAPTER_TYPE | null) => {
      setConnectedAdapterName(adapterName);
      // TODO: remove these and replace authenticateStatus with connectedAdapterName context in other places
      if (!adapterName) {
        dispatch(setAuthenticateStatus(AuthenticateState.PENDING));
      } else if (adapterName === SUPPORTED_ADAPTERS.GUEST) {
        dispatch(setAuthenticateStatus(AuthenticateState.GUEST));
      } else if (adapterName === SUPPORTED_ADAPTERS.GOOGLE) {
        setIsProticoAuthenticated(true);
        dispatch(setAuthenticateStatus(AuthenticateState.WALLET));
      } else {
        dispatch(setAuthenticateStatus(AuthenticateState.WALLET));
      }
    },
    [dispatch]
  );

  const { authenticateStatus } = useAppSelector((state) => state.auth);

  // Handle chain change event
  const handleChainChanged = ({ chainId }: { chainId: string }) => {
    console.log("Chain changed event", chainId);
    setChainId(chainId);
  };
  const handleAccountChanged = ({ accounts }: { accounts: string[] }) => {
    console.log("Account changed event", accounts);
    if (accounts[0]) handleSetAddress(accounts[0], accounts[0]);
  };
  const handleConnected = async (data: CONNECTED_EVENT_DATA) => {
    handleSetAddress(
      data.status?.address ? data.status.address.toLowerCase() : null,
      realAddress ? realAddress : null
    );
    if (data.status?.chainId) setChainId(data.status.chainId);
    setProvider(proticoAuth.current.provider);
    providerRef.current = proticoAuth.current.provider;
    handleSetConnectedAdapterName(proticoAuth.current.connectedAdapterName);
  };
  const isLocalStorageEnalbed = React.useMemo(() => localStorageEnalbed(), []);

  React.useEffect(() => {
    const init = (isLocalStorageEnalbed: boolean) => {
      proticoAuth.current = new ProticoAuthCore({
        chainId: "0x1",
        enableLogging: true,
        web3AccessTokenServer:
          NODE_ENV === "production"
            ? "https://" + DAPP_DOMAIN
            : "http://" + DAPP_DOMAIN,
        adapterConfigs: {
          [SUPPORTED_ADAPTERS.METAMASK]: { enable: true },
          [SUPPORTED_ADAPTERS.WALLET_CONNECT_V2]: {
            enable: isLocalStorageEnalbed,
            projectId: wcProjectId,
          },
          [SUPPORTED_ADAPTERS.OPENLOGIN]: {
            enable: true,
            adapterSettings: {
              clientId,
              web3AuthNetwork,
              uxMode: "popup",
              loginConfig: {
                google: {
                  name: "Protico",
                  verifier: "protico-google",
                  typeOfLogin: "google",
                  clientId:
                    "161771605130-aimaj0jik9ek180gotll9ve5nesmhe9t.apps.googleusercontent.com",
                },
              },
            },
          },
        },
      });
      proticoAuth.current!.on(
        PROTICO_AUTH_EVENTS.CHAIN_CHANGED,
        handleChainChanged
      );
      proticoAuth.current!.on(PROTICO_AUTH_EVENTS.CONNECTED, handleConnected);
      proticoAuth.current!.on(
        PROTICO_AUTH_EVENTS.ACCOUNT_CHANGED,
        handleAccountChanged
      );
      proticoAuth.current.init(true).then(() => {
        setIsInited(true);
      });
    };
    if (!proticoAuth.current) init(isLocalStorageEnalbed);
  }, []);

  React.useEffect(() => {
    // Respond to the `storage` event
    function storageEventHandler(event: { key: string; newValue: string }) {
      if (
        event.key === "protico-auth-cached-adapter" &&
        event.newValue &&
        !providerRef.current
      ) {
        const adapter_value = JSON.parse(event.newValue);
        login(convertStringToAdapter(adapter_value));
      }
    }
    // Hook up the event handler
    window.addEventListener("storage", storageEventHandler);
  }, []);

  function convertStringToAdapter(item: string): SUPPORTED_ADAPTER_TYPE | null {
    if (
      item == SUPPORTED_ADAPTERS.METAMASK ||
      item == SUPPORTED_ADAPTERS.GUEST ||
      item == SUPPORTED_ADAPTERS.WALLET_CONNECT_V2 ||
      item == SUPPORTED_ADAPTERS.OPENLOGIN
    ) {
      return item;
    }
    return null;
  }

  const login = async (adapter: SUPPORTED_ADAPTER_TYPE | null) => {
    await doLogin(adapter);
  };

  const loginAsGuest = async () => {
    await doLogin(SUPPORTED_ADAPTERS.GUEST);
  };

  const doLogin = async (
    adapter: SUPPORTED_ADAPTER_TYPE | null,
    address?: string
  ) => {
    if (!proticoAuth.current) return;
    try {
      if (adapter == SUPPORTED_ADAPTERS.GOOGLE) {
        await proticoAuth.current.connectTo(adapter, address);
      } else {
        await proticoAuth.current.connectTo(adapter);
      }
      if (previousPage) {
        push({
          pathname: previousPage,
          search: location.search,
        });
      }
    } catch (err) {
      setConnectedAdapterName(null);
      throw err;
    }
  };
  const logout = async (previousPage?: string, showModel?: boolean) => {
    setProvider(null);
    setPreviousPage(previousPage);
    setShowModal(showModel || false);
    providerRef.current = null;

    // Reset guest promotion suppress flag so users can see the modal again in new sessions
    // Note: Don't clear guest linking data here as it's needed for post-login processing
    try {
      // Clear the 7-day suppression flag
      localStorage.removeItem("protico_guest_promotion_suppress_until");
      // Only clear guest linking data if user isn't in the middle of linking process
      const hasLinkingData =
        localStorage.getItem("protico_guest_link_choice") ||
        localStorage.getItem("protico_guest_data_to_link");
      if (!hasLinkingData) {
        // No linking in progress, safe to clear pending messages
        localStorage.removeItem("protico_pending_guest_message");
        localStorage.removeItem("protico_pending_message_type");
        localStorage.removeItem("protico_pending_message");
        localStorage.removeItem("protico_pending_mentions");
      }
      // Don't clear protico_guest_link_choice and protico_guest_data_to_link here
      // They will be cleaned up by useGuestDataLinking hook after processing
    } catch (error) {
      console.warn("Failed to cleanup guest data on logout:", error);
    }

    if (!proticoAuth.current) return;
    await proticoAuth.current.disconnect();
    handleSetConnectedAdapterName(null);
  };

  const switchChain = async (chainId: string) => {
    if (!proticoAuth.current) return;
    await proticoAuth.current.switchChain(chainId);
  };

  const proticoAuthenticate = async () => {
    if (!proticoAuth.current) return;
    try {
      const userToken = await getProticoClient().getWeb3AccessToken(
        proticoAuth.current
      );
      const { success, userInfo: tokenUserInfo } =
        await getProticoClient().setAuthToken(
          userToken.web3AccessToken,
          roomDomain
        );
      if (success) {
        setCurrentToken(userToken);
        setUserInfo(tokenUserInfo);
        handleSetAddress(tokenUserInfo.address, tokenUserInfo.real_address);
      }
      setIsProticoAuthenticated(true);
    } catch (err) {
      console.log(err);
    }
  };

  const proticoGoogleAuthenticate = async (
    userToken: string,
    exp: number,
    address: string
  ) => {
    if (!proticoAuth.current) return;
    try {
      const { success, userInfo: tokenUserInfo } =
        await getProticoClient().setAuthToken(userToken, roomDomain);
      localStorage.setItem(
        "protico-auth-cached-adapter",
        JSON.stringify("googlelogin")
      );
      localStorage.setItem(
        `${tokenUserInfo.address.toLowerCase()}_${"googlelogin"}`,
        JSON.stringify({ token: userToken, exp: exp })
      );
      await doLogin(SUPPORTED_ADAPTERS.GOOGLE, address);
      if (success) {
        setUserInfo(tokenUserInfo);
        handleSetAddress(tokenUserInfo.address, tokenUserInfo.real_address);
      }
      setIsProticoAuthenticated(true);
    } catch (err) {
      console.log(err);
    }
  };

  const proticoAuthenticateAutoStorage = React.useCallback(async () => {
    if (proticoAuth.current.connectedAdapterName != SUPPORTED_ADAPTERS.GOOGLE) {
      if (!provider || !address || !proticoAuth.current) {
        setIsProticoAuthenticated(false);
        getProticoClient().clearAuthToken();
        window.parent?.postMessage("last address login:delete", "*");
        return;
      }
    }
    const userTokenInStorage =
      await getProticoClient().web3AccessTokenAutoStorage(proticoAuth.current);
    if (!userTokenInStorage) {
      setIsProticoAuthenticated(false);
      getProticoClient().clearAuthToken();
      window.parent?.postMessage("last address login:delete", "*");
      return;
    }
    const { success, userInfo: tokenUserInfo } =
      await getProticoClient().setAuthToken(
        userTokenInStorage.web3AccessToken,
        roomDomain
      );
    if (success) {
      setCurrentToken(userTokenInStorage);
      handleSetAddress(tokenUserInfo.address, tokenUserInfo.real_address);
      setUserInfo(tokenUserInfo);
      setIsProticoAuthenticated(true);
      window.parent?.postMessage(
        "last address login:" + tokenUserInfo.uuid,
        "*"
      );
    }
  }, [provider, address, proticoAuth.current, roomDomain]);

  const handlePageViewEvent = (queryUrl: string) => {
    getProticoClient().engagement.recordPageViewEvent({
      url: queryUrl,
      iframe_disable: query.protico_disable === "true" || query.protico_disable === "True" ? true : false,
    });
  };

  const handleLPageViewEventForRealPageUrlWhenLobbyOpen = (
    pageViewUrl: string
  ) => {
    getProticoClient().engagement.recordPageViewEvent({
      url: pageViewUrl,
      iframe_disable: query.protico_disable === "true" || query.protico_disable === "True" ? true : false,
    });
  };

  React.useEffect(() => {
    let roomUrl;
    // @ts-ignore
    if (typeof query.roomUrl !== "undefined") {
      // @ts-ignore
      roomUrl = query.roomUrl as string;
    } else {
      // dev mode
      roomUrl = parseUrl(document.location.href);
    }
    if (roomUrl) {
      // Call the function with the roomUrl which we get when the page is loaded ( Lobby is not opened )
      handlePageViewEvent(roomUrl);
    }
  }, []);

  React.useEffect(() => {
    if (!isInited || !pageUrl) return;
    // Call the function with the pageUrl which we get only after the pageUrl is set ( Looby Opened )
    handleLPageViewEventForRealPageUrlWhenLobbyOpen(pageUrl);
  }, [isInited, pageUrl]);

  React.useEffect(() => {
    if (roomDomain) {
      proticoAuthenticateAutoStorage();
    }
  }, [proticoAuthenticateAutoStorage, roomDomain]);

  React.useEffect(() => {
    // clear local storage for walletconnect v1 deeplink
    const deeplink = loadState(LocalStorageKeys.WALLETCONNECT_DEEPLINK_CHOICE);
    if (deeplink?.href && deeplink.href.includes("@1")) {
      console.log(
        "clear local storage for walletconnect v1 deeplink: ",
        deeplink
      );
      clearState(LocalStorageKeys.WALLETCONNECT_DEEPLINK_CHOICE);
    }
  }, []);

  return (
    <Web3AuthContext.Provider
      value={{
        login,
        loginAsGuest,
        logout,
        switchChain,
        logined:
          !!connectedAdapterName &&
          authenticateStatus !== AuthenticateState.PENDING,
        address,
        chainId,
        provider,
        connectedAdapterName,
        proticoAuthenticate,
        proticoGoogleAuthenticate,
        isProticoAuthenticated,
        isInited,
        userInfo,
        realAddress,
        currentToken,
        showModal,
        setShowModal,
      }}
    >
      {children}
    </Web3AuthContext.Provider>
  );
};
